import React from 'react'
import { useTranslation } from 'react-i18next'
import { makeStyles, withStyles } from '@material-ui/core/styles'
import Slider from '@material-ui/core/Slider'
import Tooltip from '@material-ui/core/Tooltip'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    padding: theme.spacing(0, 1),
  },
  labelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
  label: {
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: theme.palette.primary.main,
  },
  info: {
    fontSize: '1rem',
    color: theme.palette.text.secondary,
    cursor: 'help',
  },
  slider: {
    color: theme.palette.primary.main,
  },
}))

const HtmlTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(13),
    border: '1px solid #dadde9',
    boxShadow: theme.shadows[1],
  },
}))(Tooltip)

const MARKS = [
  { value: 0, label: '0%' },
  { value: 50, label: '50%' },
  { value: 100, label: '100%' },
]

/**
 * Max cloud-coverage control shown on a 0–100% scale.
 *
 * The backend / FIS hook expects a 0–1 fraction, so this component converts:
 * it displays percent for the user and commits a fraction via `onCommitFraction`
 * on release (avoids refetching the index on every drag tick).
 */
export const CloudCoverageControl = ({
  valueFraction,
  onCommitFraction,
  disabled = false,
  info = true,
}) => {
  const classes = useStyles()
  const { t } = useTranslation()

  const toPercent = (frac) => Math.round((Number(frac) || 0) * 100)
  const [display, setDisplay] = React.useState(toPercent(valueFraction))

  // Keep the slider in sync if the parent changes maxCC externally.
  React.useEffect(() => {
    setDisplay(toPercent(valueFraction))
  }, [valueFraction])

  return (
    <div className={classes.root}>
      <div className={classes.labelRow}>
        <span className={classes.label}>{t('Max Cloud Coverage')}</span>
        {info && (
          <HtmlTooltip title={<>{t('Cloud info')}</>} placement="top">
            <InfoOutlinedIcon className={classes.info} />
          </HtmlTooltip>
        )}
      </div>
      <Slider
        className={classes.slider}
        value={display}
        onChange={(_e, v) => setDisplay(v)}
        onChangeCommitted={(_e, v) => onCommitFraction(v / 100)}
        valueLabelDisplay="auto"
        valueLabelFormat={(v) => `${v}%`}
        getAriaValueText={(v) => `${v}%`}
        step={5}
        marks={MARKS}
        min={0}
        max={100}
        disabled={disabled}
      />
    </div>
  )
}
